import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, Panel, Loader } from "@/components/portal/Shared";
import { EvidenceBadge } from "@/components/portal/EvidenceBadge";
import { MapContainer, TileLayer, Polygon, Polyline, CircleMarker, Popup, Tooltip } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { TERRITORIES, TRAIL_OF_TEARS } from "@/lib/mapData";
import { toast } from "sonner";
import { Info, MapPinPlus, Loader2, X, MapPin, Feather } from "lucide-react";

const LAYER_META = [
  { id: "records", label: "Record Pins", color: "#B91C1C", default: true },
  { id: "ancestors", label: "My Ancestors", color: "#D4AF37", default: true },
  { id: "territories", label: "My Affiliation Homeland", color: "#D4AF37", default: true },
  { id: "reservations", label: "Indian Territory / Relocation", color: "#10B981", default: true },
  { id: "migration", label: "Trail of Tears Route", color: "#EF4444", default: true },
];

const YEARS = [1750, 1800, 1830, 1860, 1880, 1900, "Today"];

const tribeKeyword = (aff) => {
  const a = (aff || "").toLowerCase();
  if (a.includes("cherokee")) return "Cherokee";
  if (a.includes("choctaw")) return "Choctaw";
  if (a.includes("seminole")) return "Seminole";
  if (a.includes("creek") || a.includes("muscogee") || a.includes("muskogee")) return "Creek";
  return null;
};

export default function HistoricalMap() {
  const [ready, setReady] = useState(false);
  const [year, setYear] = useState(1900);
  const [layers, setLayers] = useState(() => Object.fromEntries(LAYER_META.map((l) => [l.id, l.default])));
  const [docs, setDocs] = useState([]);
  const [recordPins, setRecordPins] = useState([]);
  const [selDoc, setSelDoc] = useState("");
  const [placeOverride, setPlaceOverride] = useState("");
  const [dropping, setDropping] = useState(false);
  const [homelandTribe, setHomelandTribe] = useState(null);
  const [affLoaded, setAffLoaded] = useState(false);
  const [ancestorPins, setAncestorPins] = useState([]);
  const numericYear = year === "Today" ? 2026 : year;

  const loadPins = () => api.get("/map-pins").then((r) => setRecordPins(r.data));

  useEffect(() => {
    Promise.all([
      api.get("/documents"),
      api.get("/map-pins"),
      api.get("/ancestor-map-pins"),
    ]).then(([d, p, ap]) => {
      setDocs(d.data);
      setRecordPins(p.data);
      setAncestorPins(ap.data);
      if (d.data[0]) setSelDoc(d.data[0].id);
      setReady(true);
    }).catch(() => setReady(true));
    api.get("/tribal-affiliation/history")
      .then((r) => { setHomelandTribe(tribeKeyword(r.data?.[0]?.result?.primary_affiliation)); setAffLoaded(true); })
      .catch(() => setAffLoaded(true));
  }, []);

  const selectedDoc = docs.find((d) => d.id === selDoc);
  useEffect(() => { setPlaceOverride(selectedDoc?.place || ""); }, [selDoc]); // eslint-disable-line

  const dropPin = async () => {
    if (!selDoc) return;
    setDropping(true);
    try {
      const r = await api.post("/map-pins", { document_id: selDoc, place: placeOverride || undefined });
      toast.success("Record pin dropped", { description: `${r.data.title} — ${r.data.place}` });
      setLayers((s) => ({ ...s, records: true }));
      await loadPins();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not drop pin");
    } finally { setDropping(false); }
  };

  const removePin = async (id) => {
    await api.delete(`/map-pins/${id}`);
    await loadPins();
  };

  const territories = useMemo(() =>
    (homelandTribe ? TERRITORIES.filter((t) => t.id === "reservations" && layers.reservations
      && (!t.fromYear || numericYear >= t.fromYear)
      && (!t.untilYear || numericYear <= t.untilYear + 6)) : []), [layers, numericYear, homelandTribe]);

  const homelandTerritory = useMemo(() =>
    (homelandTribe && layers.territories) ? TERRITORIES.find((t) => t.id === "territories" && t.tribe === homelandTribe) : null,
    [homelandTribe, layers.territories]);

  const pins = useMemo(() =>
    layers.ancestors ? ancestorPins : [], [layers.ancestors, ancestorPins]);

  const shownRecordPins = useMemo(() =>
    layers.records ? recordPins.filter((p) => !p.year || numericYear >= p.year - 1) : [], [layers, recordPins, numericYear]);

  if (!ready) return <Loader />;

  return (
    <div data-testid="module-historical-map">
      <PageHeader
        eyebrow="Research • Understand"
        title="Historical Territory Map"
        subtitle="This map is built from your own records: your Tribal Affiliation homeland, your family members' documented places, and pins you drop from your documents. A visual overlap is a research lead — never automatic proof of descent."
      />

      {homelandTerritory && (
        <div data-testid="homeland-highlight-banner" className="mb-5 rounded-xl bg-[#F3EEFA] border border-[#59288E]/25 p-3 text-sm text-[#3F1E68] flex items-center gap-2">
          <MapPin size={16} className="text-[#59288E] shrink-0" /> Showing your affiliation homeland — <span className="font-semibold">{homelandTerritory.name}</span>, drawn from your Tribal Affiliation report.
        </div>
      )}
      {affLoaded && !homelandTribe && (
        <div data-testid="no-affiliation-banner" className="mb-5 rounded-xl bg-[#FFF9E6] border border-[#D4AF37]/40 p-3 text-sm text-[#8F7220] flex items-center gap-2">
          <Feather size={16} className="text-[#C59B27] shrink-0" /> No homeland is mapped yet. Generate your <span className="font-semibold">Tribal Affiliation report</span> and this map will highlight your nation's historic territory.
        </div>
      )}

      <div className="grid lg:grid-cols-4 gap-6">
        <div className="lg:col-span-1 space-y-4">
          {/* Drop a record pin */}
          <Panel className="p-5" data-testid="drop-pin-panel">
            <div className="flex items-center gap-2 mb-3 text-[#180D24]">
              <MapPinPlus size={18} className="text-[#B91C1C]" />
              <span className="font-serif font-semibold">Drop a Record Pin</span>
            </div>
            <label className="text-[10px] uppercase font-mono tracking-wider text-[#8F7220]">Document</label>
            <select
              data-testid="pin-doc-select"
              value={selDoc}
              onChange={(e) => setSelDoc(e.target.value)}
              className="mt-1 w-full rounded-lg border border-[#E4DCD3] bg-white px-2.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50"
            >
              {docs.map((d) => <option key={d.id} value={d.id}>{d.title}{d.year ? ` (${d.year})` : ""}</option>)}
            </select>
            <label className="text-[10px] uppercase font-mono tracking-wider text-[#8F7220] mt-3 block">Place</label>
            <input
              data-testid="pin-place-input"
              value={placeOverride}
              onChange={(e) => setPlaceOverride(e.target.value)}
              placeholder="e.g. Tishomingo, Oklahoma"
              className="mt-1 w-full rounded-lg border border-[#E4DCD3] px-2.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50"
            />
            <button
              data-testid="drop-pin-btn"
              onClick={dropPin}
              disabled={dropping || !selDoc}
              className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#2A1442] text-white px-4 py-2.5 text-sm font-semibold hover:bg-[#482276] transition-colors disabled:opacity-60"
            >
              {dropping ? <Loader2 size={15} className="animate-spin" /> : <MapPinPlus size={15} />}
              Drop Pin on Map
            </button>
            {recordPins.length > 0 && (
              <div className="mt-4">
                <div className="text-[10px] uppercase font-mono tracking-wider text-[#8F7220] mb-1.5">Dropped Pins ({recordPins.length})</div>
                <div className="space-y-1.5 max-h-40 overflow-y-auto hp-scroll">
                  {recordPins.map((p) => (
                    <div key={p.id} className="flex items-center gap-2 text-xs bg-[#FAF8F5] border border-[#E4DCD3] rounded-lg px-2 py-1.5" data-testid={`record-pin-item-${p.id}`}>
                      <span className="h-2 w-2 rounded-full bg-[#B91C1C] shrink-0" />
                      <span className="flex-1 truncate text-[#1A171D]" title={p.title}>{p.place} · {p.year || "?"}</span>
                      <button onClick={() => removePin(p.id)} className="text-[#9CA3AF] hover:text-[#B91C1C]" data-testid={`remove-pin-${p.id}`}><X size={13} /></button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Panel>

          {/* Overlays */}
          <Panel className="p-5">
            <h3 className="font-serif text-lg font-semibold text-[#180D24] mb-3">Overlays</h3>
            <div className="space-y-2.5">
              {LAYER_META.map((l) => (
                <label key={l.id} className="flex items-center gap-3 cursor-pointer text-sm" data-testid={`layer-${l.id}`}>
                  <input type="checkbox" checked={!!layers[l.id]}
                    onChange={(e) => setLayers((s) => ({ ...s, [l.id]: e.target.checked }))}
                    className="accent-[#59288E] h-4 w-4" />
                  <span className="h-3 w-3 rounded-full" style={{ backgroundColor: l.color }} />
                  <span className="text-[#1A171D]">{l.label}</span>
                </label>
              ))}
            </div>
            <div className="hp-gold-rule my-4" />
            <div className="flex items-start gap-2 text-[11px] text-[#756D7E]">
              <Info size={26} className="text-[#8F7220] shrink-0 -mt-0.5" />
              <span>Territory outlines are approximate, illustrative extents for education — not surveyed legal boundaries.</span>
            </div>
          </Panel>
        </div>

        <Panel className="lg:col-span-3 p-0 overflow-hidden">
          <div className="h-[56vh]" data-testid="map-canvas">
            <MapContainer center={[34.5, -90]} zoom={5} scrollWheelZoom style={{ height: "100%", width: "100%" }}>
              <TileLayer
                attribution='&copy; OpenStreetMap contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {territories.filter((t) => t !== homelandTerritory).map((t, i) => (
                <Polygon key={`t-${t.tribe}-${i}`} positions={t.coords} pathOptions={{ color: t.color, fillColor: t.color, fillOpacity: 0.22, weight: 2 }}>
                  <Tooltip sticky>{t.name}</Tooltip>
                </Polygon>
              ))}
              {homelandTerritory && (
                <Polygon positions={homelandTerritory.coords} pathOptions={{ color: "#D4AF37", fillColor: "#D4AF37", fillOpacity: 0.3, weight: 4, dashArray: "6 4" }}>
                  <Tooltip sticky>{homelandTerritory.name} — your affiliation homeland</Tooltip>
                </Polygon>
              )}
              {layers.migration && homelandTribe && (
                <Polyline positions={TRAIL_OF_TEARS.path} pathOptions={{ color: TRAIL_OF_TEARS.color, weight: 4, dashArray: "8 6" }}>
                  <Tooltip sticky>{TRAIL_OF_TEARS.name}</Tooltip>
                </Polyline>
              )}
              {pins.map((p, i) => (
                <CircleMarker key={`a-${i}`} center={[p.lat, p.lng]} radius={8}
                  pathOptions={{ color: "#180D24", fillColor: "#D4AF37", fillOpacity: 1, weight: 2 }}>
                  <Popup>
                    <div className="font-semibold text-[#180D24]">{p.name}</div>
                    <div className="text-xs text-[#756D7E] mt-1">{p.note}</div>
                    <div className="text-[11px] font-mono text-[#8F7220] mt-1">Year: {p.year}</div>
                  </Popup>
                </CircleMarker>
              ))}
              {shownRecordPins.map((p) => (
                <CircleMarker key={`r-${p.id}`} center={[p.lat, p.lng]} radius={9}
                  pathOptions={{ color: "#180D24", fillColor: "#B91C1C", fillOpacity: 1, weight: 2 }}>
                  <Popup>
                    <div className="font-semibold text-[#180D24]">{p.title}</div>
                    <div className="text-xs text-[#756D7E] mt-1">{p.place}{p.ancestor ? ` · ${p.ancestor}` : ""}</div>
                    <div className="text-[11px] font-mono text-[#8F7220] mt-1">Record year: {p.year || "unknown"}</div>
                  </Popup>
                </CircleMarker>
              ))}
            </MapContainer>
          </div>

          <div className="p-5 border-t border-[#E4DCD3]">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs uppercase font-mono tracking-wider text-[#8F7220]">Time Navigation</span>
              <span className="font-serif text-xl font-bold text-[#59288E]" data-testid="map-year">{year}</span>
            </div>
            <input type="range" min={0} max={YEARS.length - 1} value={YEARS.indexOf(year)}
              onChange={(e) => setYear(YEARS[+e.target.value])}
              className="w-full accent-[#D4AF37]" data-testid="map-year-slider" />
            <div className="flex justify-between mt-1 text-[10px] font-mono text-[#756D7E]">
              {YEARS.map((y) => <span key={y}>{y}</span>)}
            </div>
          </div>
        </Panel>
      </div>
    </div>
  );
}
